import { externalNetworkClient } from '@terra/shared-rest-clients-external-network';
import { internalNetworkClient } from '@terra/shared-rest-clients-internal-network';
import type { ExternalNetworkContract } from '@terra/shared-contracts-external-network';
import type {
  DomainListQueryDto,
  GatewayListQueryDto,
  HostListQueryDto,
  NetBlockListQueryDto,
  NetworkServiceListQueryDto,
  SoftwareListQueryDto,
  StorageServiceListQueryDto,
} from '@terra/shared-contracts-external-network';
import type { InternalNetworkContract } from '@terra/shared-contracts-internal-network';
import type {
  CreateInternalCrownJewelDto,
  CreateInternalOwnedPrincipalDto,
} from '@terra/shared-contracts-internal-network';
import { tenantAwareApiWrapper, withOrchestratorClient } from './api-core';

export const surfaceAssetsApi = {
  getExternalNetworkStats: (surfaceId: string) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getStats>({
      endpointFn: (headers) =>
        externalNetworkClient.getStats({
          params: { surfaceId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listSurfaceDomains: ({
    surfaceId,
    query,
  }: {
    surfaceId: string;
    query?: DomainListQueryDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.listDomains>({
      endpointFn: (headers) =>
        externalNetworkClient.listDomains({
          params: { surfaceId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),

  getSurfaceDomain: ({
    surfaceId,
    domainId,
  }: {
    surfaceId: string;
    domainId: string;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getDomain>({
      endpointFn: (headers) =>
        externalNetworkClient.getDomain({
          params: { surfaceId, domainId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listSurfaceHosts: ({
    surfaceId,
    query,
  }: {
    surfaceId: string;
    query?: HostListQueryDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.listHosts>({
      endpointFn: (headers) =>
        externalNetworkClient.listHosts({
          params: { surfaceId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),

  getSurfaceHost: ({
    surfaceId,
    hostId,
  }: {
    surfaceId: string;
    hostId: string;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getHost>({
      endpointFn: (headers) =>
        externalNetworkClient.getHost({
          params: { surfaceId, hostId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listSurfaceGateways: ({
    surfaceId,
    query,
  }: {
    surfaceId: string;
    query?: GatewayListQueryDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.listGateways>({
      endpointFn: (headers) =>
        externalNetworkClient.listGateways({
          params: { surfaceId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),

  getSurfaceGateway: ({
    surfaceId,
    gatewayId,
  }: {
    surfaceId: string;
    gatewayId: string;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getGateway>({
      endpointFn: (headers) =>
        externalNetworkClient.getGateway({
          params: { surfaceId, gatewayId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listSurfaceServices: ({
    surfaceId,
    query,
  }: {
    surfaceId: string;
    query?: NetworkServiceListQueryDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.listServices>({
      endpointFn: (headers) =>
        externalNetworkClient.listServices({
          params: { surfaceId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),

  getSurfaceService: ({
    surfaceId,
    serviceId,
  }: {
    surfaceId: string;
    serviceId: string;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getService>({
      endpointFn: (headers) =>
        externalNetworkClient.getService({
          params: { surfaceId, serviceId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listSurfaceNetblocks: ({
    surfaceId,
    query,
  }: {
    surfaceId: string;
    query?: NetBlockListQueryDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.listNetblocks>({
      endpointFn: (headers) =>
        externalNetworkClient.listNetblocks({
          params: { surfaceId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),

  getSurfaceNetblock: ({
    surfaceId,
    netblockId,
  }: {
    surfaceId: string;
    netblockId: string;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getNetblock>({
      endpointFn: (headers) =>
        externalNetworkClient.getNetblock({
          params: { surfaceId, netblockId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listSurfaceSoftware: ({
    surfaceId,
    query,
  }: {
    surfaceId: string;
    query?: SoftwareListQueryDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.listSoftware>({
      endpointFn: (headers) =>
        externalNetworkClient.listSoftware({
          params: { surfaceId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),

  getSurfaceSoftware: ({
    surfaceId,
    softwareId,
  }: {
    surfaceId: string;
    softwareId: string;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getSoftware>({
      endpointFn: (headers) =>
        externalNetworkClient.getSoftware({
          params: { surfaceId, softwareId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listSurfaceStorageServices: ({
    surfaceId,
    query,
  }: {
    surfaceId: string;
    query?: StorageServiceListQueryDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.listStorageServices>({
      endpointFn: (headers) =>
        externalNetworkClient.listStorageServices({
          params: { surfaceId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),

  getSurfaceStorageService: ({
    surfaceId,
    storageServiceId,
  }: {
    surfaceId: string;
    storageServiceId: string;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getStorageService>({
      endpointFn: (headers) =>
        externalNetworkClient.getStorageService({
          params: { surfaceId, storageServiceId },
          ...withOrchestratorClient(headers),
        }),
    }),

  getInternalNetworkStats: (networkId: string) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.getStats>({
      endpointFn: (headers) =>
        internalNetworkClient.getStats({
          params: { networkId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listInternalNetworkCrownJewels: (networkId: string) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.listCrownJewels>({
      endpointFn: (headers) =>
        internalNetworkClient.listCrownJewels({
          params: { networkId },
          query: {},
          ...withOrchestratorClient(headers),
        }),
    }),

  createInternalNetworkCrownJewel: ({
    networkId,
    body,
  }: {
    networkId: string;
    body: CreateInternalCrownJewelDto;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.createCrownJewel>({
      endpointFn: (headers) =>
        internalNetworkClient.createCrownJewel({
          params: { networkId },
          body,
          ...withOrchestratorClient(headers),
        }),
    }),

  deleteInternalNetworkCrownJewel: ({
    networkId,
    crownJewelId,
  }: {
    networkId: string;
    crownJewelId: string;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.deleteCrownJewel>({
      endpointFn: (headers) =>
        internalNetworkClient.deleteCrownJewel({
          params: { networkId, crownJewelId },
          ...withOrchestratorClient(headers),
        }),
    }),

  createInternalNetworkOwnedPrincipal: ({
    networkId,
    body,
  }: {
    networkId: string;
    body: CreateInternalOwnedPrincipalDto;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.createOwnedPrincipal>({
      endpointFn: (headers) =>
        internalNetworkClient.createOwnedPrincipal({
          params: { networkId },
          body,
          ...withOrchestratorClient(headers),
        }),
    }),

  revokeInternalNetworkOwnedPrincipal: ({
    networkId,
    entityId,
  }: {
    networkId: string;
    entityId: string;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.revokeOwnedPrincipal>({
      endpointFn: (headers) =>
        internalNetworkClient.revokeOwnedPrincipal({
          params: { networkId, entityId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listInternalNetworkScenariosBySignal: ({ signalId }: { signalId: string }) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.listScenariosBySignal>(
      {
        endpointFn: (headers) =>
          internalNetworkClient.listScenariosBySignal({
            params: { signalId },
            query: {},
            ...withOrchestratorClient(headers),
          }),
      },
    ),

  stopInternalNetworkScenario: ({ scenarioId }: { scenarioId: string }) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.stopScenario>({
      endpointFn: (headers) =>
        internalNetworkClient.stopScenario({
          params: { scenarioId },
          body: {},
          ...withOrchestratorClient(headers),
        }),
    }),

  listInternalNetworkOwnedPrincipals: ({
    networkId,
    skip,
    limit,
  }: {
    networkId: string;
    skip?: number;
    limit?: number;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.listOwnedPrincipals>({
      endpointFn: (headers) =>
        internalNetworkClient.listOwnedPrincipals({
          params: { networkId },
          query: { skip, limit },
          ...withOrchestratorClient(headers),
        }),
    }),

  listInternalNetworkDiscoveryRuns: ({
    networkId,
    skip,
    limit,
  }: {
    networkId: string;
    skip?: number;
    limit?: number;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.listDiscoveryRuns>({
      endpointFn: (headers) =>
        internalNetworkClient.listDiscoveryRuns({
          params: { networkId },
          query: { skip, limit },
          ...withOrchestratorClient(headers),
        }),
    }),

  getInternalNetworkSignalsTrend: ({
    networkId,
    days,
  }: {
    networkId: string;
    days?: number;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.getSignalsTrend>({
      endpointFn: (headers) =>
        internalNetworkClient.getSignalsTrend({
          params: { networkId },
          query: { days },
          ...withOrchestratorClient(headers),
        }),
    }),

  getExternalNetworkSignalsTrend: ({
    surfaceId,
    days,
  }: {
    surfaceId: string;
    days?: number;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getSignalsTrend>({
      endpointFn: (headers) =>
        externalNetworkClient.getSignalsTrend({
          params: { surfaceId },
          query: { days },
          ...withOrchestratorClient(headers),
        }),
    }),
};
